import { useMemo, useState } from "react";
import {
  FiCheckCircle,
  FiExternalLink,
  FiGithub,
  FiSearch,
  FiStar,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import DifficultyBadge from "../components/ui/DifficultyBadge";
import {
  categories,
  difficultyCounts,
  DIFFICULTY_ORDER,
  numberedProjects,
} from "../data/projects";

const Problems = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [diff, setDiff] = useState("All");
  const [cat, setCat] = useState("All");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return numberedProjects.filter((p) => {
      const matchesQ =
        !q ||
        p.title.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q) ||
        p.tags.some((t) => t.toLowerCase().includes(q));
      const matchesD = diff === "All" || p.difficulty === diff;
      const matchesC = cat === "All" || p.category === cat;
      return matchesQ && matchesD && matchesC;
    });
  }, [search, diff, cat]);

  const stopRow = (e) => e.stopPropagation();

  return (
    <div className="page problems-page">
      <div className="page-head">
        <h1 className="page-title">
          Problems <span className="page-title-alt">/ Projects</span>
        </h1>
        <div className="difficulty-summary">
          <span className="ds-item ds-total">
            Solved <b>{difficultyCounts.total}</b>/{difficultyCounts.total}
          </span>
          {DIFFICULTY_ORDER.map((d) => (
            <span key={d} className={`ds-item ds-${d.toLowerCase()}`}>
              {d} <b>{difficultyCounts[d]}</b>
            </span>
          ))}
        </div>
      </div>

      <div className="filters">
        <div className="search-box">
          <FiSearch />
          <input
            type="text"
            placeholder="Search problems by title, tag or category"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="lc-select"
          value={diff}
          onChange={(e) => setDiff(e.target.value)}
        >
          <option value="All">Difficulty</option>
          {DIFFICULTY_ORDER.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
        <select
          className="lc-select"
          value={cat}
          onChange={(e) => setCat(e.target.value)}
        >
          <option value="All">Category</option>
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>

      <div className="table-wrap">
        <table className="problems-table">
          <thead>
            <tr>
              <th className="col-status" />
              <th className="col-num">#</th>
              <th className="col-title">Title</th>
              <th className="col-cat">Category</th>
              <th className="col-tags">Topics</th>
              <th className="col-diff">Difficulty</th>
              <th className="col-links">Links</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr
                key={p.slug}
                className={`problem-row${p.featured ? " is-featured" : ""}`}
                onClick={() => navigate(`/problems/${p.slug}`)}
              >
                <td className="col-status">
                  <FiCheckCircle className="status-solved" title="Solved" />
                </td>
                <td className="col-num">{p.number}</td>
                <td className="col-title">
                  <span className="problem-link">{p.title}</span>
                  {p.featured && (
                    <span className="featured-tag">
                      <FiStar /> Featured
                    </span>
                  )}
                </td>
                <td className="col-cat">{p.category}</td>
                <td className="col-tags">
                  <div className="row-tags">
                    {p.tags.slice(0, 3).map((t) => (
                      <span key={t} className="row-tag">
                        {t}
                      </span>
                    ))}
                    {p.tags.length > 3 && (
                      <span className="row-tag row-tag-more">
                        +{p.tags.length - 3}
                      </span>
                    )}
                  </div>
                </td>
                <td className="col-diff">
                  <DifficultyBadge difficulty={p.difficulty} />
                </td>
                <td className="col-links" onClick={stopRow}>
                  {p.github && (
                    <a
                      href={p.github}
                      target="_blank"
                      rel="noreferrer"
                      className="icon-btn sm"
                      aria-label="GitHub"
                      title="GitHub"
                    >
                      <FiGithub />
                    </a>
                  )}
                  {p.demo && (
                    <a
                      href={p.demo}
                      target="_blank"
                      rel="noreferrer"
                      className="icon-btn sm"
                      aria-label="Live demo"
                      title={p.demoLabel || "Live demo"}
                    >
                      <FiExternalLink />
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filtered.length === 0 && (
          <div className="empty-state">No problems match your filters.</div>
        )}
      </div>
    </div>
  );
};

export default Problems;
